import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'retrostore_admin_token';

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState(null);

  // Validate token by making a test request to the versioning API
  const validateToken = useCallback(async (tokenToValidate) => {
    if (!tokenToValidate) {
      setIsAuthenticated(false);
      setIsValidating(false);
      return false;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/versioning/auth`, {
        headers: {
          'Authorization': `Bearer ${tokenToValidate}`
        }
      });

      if (response.ok) {
        setToken(tokenToValidate);
        setIsAuthenticated(true);
        setError(null);
        return true;
      } else {
        // Token is invalid or revoked
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
        setIsAuthenticated(false);
        setError('Invalid or expired token');
        return false;
      }
    } catch (err) {
      // Network error - keep token but mark as not validated
      console.error('Token validation failed:', err);
      setError('Network error during validation');
      return false;
    } finally {
      setIsValidating(false);
    }
  }, []);

  // Check for stored token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(STORAGE_KEY);
    if (storedToken) {
      validateToken(storedToken);
    } else {
      setIsValidating(false);
    }
  }, [validateToken]);

  // Login with a new token
  const login = useCallback(async (newToken) => {
    setIsValidating(true);
    setError(null);

    const isValid = await validateToken(newToken);

    if (isValid) {
      localStorage.setItem(STORAGE_KEY, newToken);
      return true;
    }

    return false;
  }, [validateToken]);

  // Logout and clear token
  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    token,
    isAuthenticated,
    isValidating,
    error,
    login,
    logout,
    clearError
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
